// On-chain metrics curated for the Academy intro page. Each metric ships
// with a 5-tier interpretation table (color-coded) so the page can render
// a consistent "what does this signal mean?" panel without extra logic.

export const METRICAS_ONCHAIN = [
  {
    id: "fear-greed",
    nombre: "Fear & Greed Index",
    dificultad: "basica",
    descripcion: "The most widely used index to measure overall crypto market sentiment. It combines volatility, momentum, volume, social media, dominance, and Google searches on a scale from 0 (Extreme Fear) to 100 (Extreme Greed). Warren Buffett: «Be greedy when others are fearful, and fearful when others are greedy».",
    apiUrl: "https://api.alternative.me/fng/",
    interpretacion: [
      { rango: "0-25", senal: "Extreme Fear", color: "green",
        descripcion: "Market in panic. Historically a buy signal for long-term holders. Institutions accumulate quietly." },
      { rango: "26-45", senal: "Fear", color: "lime",
        descripcion: "Negative sentiment. Retail is exiting. Opportunity to analyze bullish setups." },
      { rango: "46-55", senal: "Neutral", color: "yellow",
        descripcion: "Market in balance. No clear bias. Wait for directional confirmation." },
      { rango: "56-75", senal: "Greed", color: "orange",
        descripcion: "Moderate euphoria. Market rising but with caution. Reduce position sizes or start taking partial profits." },
      { rango: "76-100", senal: "Extreme Greed", color: "red",
        descripcion: "Maximum euphoria. Historically signals market top. Institutions are distributing. Exercise maximum caution." },
    ],
  },
  {
    id: "nupl",
    nombre: "NUPL — Net Unrealized Profit/Loss",
    dificultad: "intermedia",
    descripcion: "Measures whether Bitcoin holders are, collectively, in unrealized profit or loss. Calculated as (Market Cap − Realized Cap) / Market Cap. One of the most reliable indicators for identifying cycle tops and bottoms.",
    interpretacion: [
      { rango: "> 0.75", senal: "Euphoria", color: "red",
        descripcion: "The vast majority holds large unrealized profits. Institutional distribution zone. Historically precedes 50-80% corrections." },
      { rango: "0.5-0.75", senal: "Belief", color: "orange",
        descripcion: "Market maturing in the bull cycle. Still room to run but with higher risk." },
      { rango: "0.25-0.5", senal: "Optimism", color: "lime",
        descripcion: "Healthy bullish phase. Good entry point on pullbacks." },
      { rango: "0-0.25", senal: "Hope", color: "green",
        descripcion: "Transition. Market recovering from a bottom. Accumulation opportunity." },
      { rango: "< 0", senal: "Capitulation", color: "green",
        descripcion: "Majority is at a loss. Maximum panic. Historically cycle bottom zone. Best long-term buying opportunity." },
    ],
  },
  {
    id: "exchange-flow",
    nombre: "Exchange Inflow / Outflow",
    dificultad: "basica",
    descripcion: "Tracks Bitcoin movement to and from centralized exchanges. When holders send BTC to exchanges, it suggests intent to sell (bearish). When they withdraw BTC from exchanges to private wallets, it suggests intent to hold (bullish). Net inflows to exchanges often precede price drops.",
    interpretacion: [
      { rango: "Inflow alto", senal: "Bearish", color: "red",
        descripcion: "Heavy BTC entering exchanges. Preparation to sell. Increases short-term selling pressure." },
      { rango: "Outflow alto", senal: "Bullish", color: "green",
        descripcion: "Heavy BTC leaving exchanges. Holders storing in own wallets. Reduces available supply for selling." },
      { rango: "Flujo neutro", senal: "No Signal", color: "yellow",
        descripcion: "Wait for price confirmation. Not a trigger on its own." },
    ],
  },
  {
    id: "mvrv",
    nombre: "MVRV Ratio",
    dificultad: "intermedia",
    descripcion: "Market Value to Realized Value. Compares current market price with the average price at which holders bought their BTC (realized price). A high MVRV means holders have large unrealized profits and may sell. A low MVRV (close to 1) historically marks cycle bottoms.",
    interpretacion: [
      { rango: "MVRV > 3.5", senal: "Extreme Overvaluation", color: "red",
        descripcion: "Holders have on average 3.5x their investment in profit. Historically top zone. Only happened 3 times in BTC history." },
      { rango: "MVRV 2-3.5", senal: "Warning", color: "orange",
        descripcion: "Market expensive but can continue. Gradually reduce exposure." },
      { rango: "MVRV 1-2", senal: "Healthy", color: "lime",
        descripcion: "Market reasonably valued." },
      { rango: "MVRV < 1", senal: "Capitulation", color: "green",
        descripcion: "BTC trading below the average purchase price of all holders. Historically the best long-term buying opportunity zone." },
    ],
  },
  {
    id: "dominancia-btc",
    nombre: "Bitcoin Dominance (BTC.D)",
    dificultad: "basica",
    descripcion: "Percentage of total crypto market cap that Bitcoin represents. Key indicator to know when to rotate between BTC and altcoins. Typical cycle: BTC rises first (D rises) → ETH and large caps follow → small altcoins explode (D falls) → everything collapses → BTC leads again.",
    apiUrl: "https://api.coingecko.com/api/v3/global",
    interpretacion: [
      { rango: "D > 60% subiendo", senal: "BTC Season", color: "orange",
        descripcion: "Capital flowing into BTC. Conservative market. Time to hold more BTC than altcoins." },
      { rango: "D 50-60%", senal: "Transition", color: "yellow",
        descripcion: "BTC consolidating leadership. Start looking at ETH and large caps." },
      { rango: "D 40-50% bajando", senal: "Altcoin Season", color: "lime",
        descripcion: "Rotate into quality altcoins. ETH, SOL, BNB leading." },
      { rango: "D < 40%", senal: "Altcoin Euphoria", color: "green",
        descripcion: "Full altcoin season. Small caps multiplying. Maximum risk — next move is usually an altcoin crash." },
    ],
  },
  {
    id: "puell-multiple",
    nombre: "Puell Multiple",
    dificultad: "avanzada",
    descripcion: "Measures Bitcoin miner profitability by comparing the daily USD value of mined BTC with the annual average. Miners are structural BTC sellers to cover operational costs. A high Puell means miners are highly profitable and may sell more. A very low Puell historically marks bottoms.",
    interpretacion: [
      { rango: "Puell > 4", senal: "Cycle Top", color: "red",
        descripcion: "Miners highly profitable. High selling pressure from miners. Historically top zone (only in extreme bull markets)." },
      { rango: "Puell 1-4", senal: "Active Market", color: "orange",
        descripcion: "Production and demand balanced." },
      { rango: "Puell 0.3-0.5", senal: "Miner Capitulation", color: "green",
        descripcion: "Miners in distress. Weakest ones surrender and price usually bottoms. Historically a cycle buying opportunity." },
    ],
  },
];

export const COLOR_DOT = {
  green: "bg-emerald-400",
  lime: "bg-lime-400",
  yellow: "bg-yellow-400",
  orange: "bg-orange-400",
  red: "bg-red-400",
};

export const DIFICULTAD_COLOR = {
  basica: "border-emerald-400/40 bg-emerald-400/[0.08] text-emerald-200",
  intermedia: "border-amber-400/40 bg-amber-400/[0.08] text-amber-200",
  avanzada: "border-red-400/40 bg-red-400/[0.08] text-red-200",
};

export const RECURSOS_EXTERNOS = [
  { nombre: "Glassnode", url: "https://glassnode.com",
    desc: "The professional standard for on-chain metrics. Limited free plan, premium for institutions." },
  { nombre: "CryptoQuant", url: "https://cryptoquant.com",
    desc: "Focus on exchange flows and miner indicators. Good free plan." },
  { nombre: "LookIntoBitcoin", url: "https://www.lookintobitcoin.com",
    desc: "Historical BTC cycle indicators. 100% free and very educational." },
];